// =============================================================================
// EmojiManagerModal — view, upload, and delete server custom emoji.
// =============================================================================
// Opened from the channel-list header by users with manage_channels
// permission (owner/admin/moderator). Shows existing emoji as a grid with
// names + delete buttons; an upload form below lets you add a new one.
//
// Upload flow (3-step, matches existing avatar/icon pattern):
//   1. Validate locally (file type, size <= 256 KB)
//   2. POST /api/uploads/presign-image  with kind='emoji' → storageKey + URL
//   3. PUT the file directly to MinIO at the presigned URL
//   4. POST /api/servers/:id/emojis with { name, storageKey } — backend resizes
//      to 128x128 + 32x32, deletes the source, inserts the row
// =============================================================================

import { useEffect, useRef, useState } from 'react';
import { Modal } from './ui';
import { Button, Spinner, Icon } from './ui';
import { api, ApiError } from '../api';
import { useServerEmojis } from '../hooks';
import type { ServerEmoji } from '../types';
import { ConfirmDialog } from './ConfirmDialog';

const MAX_EMOJI_BYTES = 256 * 1024;
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

export function EmojiManagerModal({
  open,
  onClose,
  serverId,
  serverName,
}: {
  open: boolean;
  onClose: () => void;
  serverId: string;
  serverName: string;
}) {
  const { emojis, loading } = useServerEmojis(serverId);

  return (
    <Modal open={open} onClose={onClose} title={`Custom emoji — ${serverName}`}>
      <div className="space-y-4">
        <p className="text-xs text-text-muted">
          Members can use these in messages and reactions by typing
          <code className="font-mono bg-elevated px-1 mx-0.5 rounded-sm">:name:</code>
          — uploads are resized to 128×128 (full) and 32×32 (inline). PNG, JPEG, GIF,
          or WebP up to 256&nbsp;KB.
        </p>

        <UploadEmojiForm serverId={serverId} existingNames={emojis.map((e) => e.name)} />

        <div className="border-t border-border-soft pt-3">
          <div className="text-[10px] uppercase tracking-wider text-text-subtle font-bold mb-2 flex items-center gap-2">
            <span>Current emoji</span>
            <span className="text-text-muted font-mono normal-case tracking-normal">
              {emojis.length}
            </span>
          </div>
          {loading ? (
            <div className="flex justify-center py-4">
              <Spinner size={16} />
            </div>
          ) : emojis.length === 0 ? (
            <div className="text-xs text-text-subtle italic text-center py-4">
              No custom emoji yet. Upload one above to get started.
            </div>
          ) : (
            <EmojiGrid emojis={emojis} />
          )}
        </div>

        <div className="flex justify-end pt-1">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </Modal>
  );
}

// =============================================================================
// UploadEmojiForm — file picker + name input + upload button
// =============================================================================
function UploadEmojiForm({
  serverId,
  existingNames,
}: {
  serverId: string;
  existingNames: string[];
}) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Object URL for the local preview. Revoke on cleanup so we don't leak memory.
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  function pickFile(f: File | null) {
    setError(null);
    if (!f) {
      setFile(null);
      return;
    }
    if (!ACCEPTED_TYPES.includes(f.type)) {
      setError('Only PNG, JPEG, GIF, or WebP images are allowed.');
      return;
    }
    if (f.size > MAX_EMOJI_BYTES) {
      setError(
        `File too large (${Math.round(f.size / 1024)} KB). Max ${MAX_EMOJI_BYTES / 1024} KB.`
      );
      return;
    }
    setFile(f);
    // Default name from filename if user hasn't entered one yet.
    if (!name) {
      const base = (f.name.split('.').slice(0, -1).join('.') || f.name)
        .toLowerCase()
        .replace(/[^a-z0-9_]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
      if (/^[a-z0-9_]{2,32}$/.test(base)) setName(base);
    }
  }

  async function upload() {
    if (!file) return;
    setError(null);
    const cleaned = name.trim().toLowerCase();
    if (!/^[a-z0-9_]{2,32}$/.test(cleaned)) {
      setError('Name must be 2–32 characters: lowercase letters, digits, underscore.');
      return;
    }
    if (existingNames.includes(cleaned)) {
      setError('That name is already used in this server.');
      return;
    }

    setUploading(true);
    try {
      // 1. Presign
      const presign = await api.presignImage({
        kind: 'emoji',
        serverId,
        contentType: file.type,
        sizeBytes: file.size,
      });
      // 2. Upload to MinIO
      const putRes = await fetch(presign.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!putRes.ok) {
        throw new Error(`Upload failed (${putRes.status})`);
      }
      // 3. Commit — backend resizes + persists
      await api.createServerEmoji(serverId, {
        name: cleaned,
        storageKey: presign.storageKey,
      });
      // Reset for next upload. The list refresh happens via WS event.
      setFile(null);
      setName('');
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="space-y-2">
      <div className="text-[10px] uppercase tracking-wider text-text-subtle font-bold">
        Upload new emoji
      </div>
      <div className="bevel-down rounded-aim bg-elevated p-3 space-y-2">
        <div className="flex items-center gap-3">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="preview"
              className="h-12 w-12 object-contain rounded-sm border border-border-soft bg-bg"
            />
          ) : (
            <div className="h-12 w-12 rounded-sm border border-dashed border-border-soft bg-bg flex items-center justify-center text-text-subtle">
              <Icon name="smile" size={20} />
            </div>
          )}
          <div className="flex-1">
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPTED_TYPES.join(',')}
              onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
              className="block text-xs text-text-muted file:mr-2 file:py-1 file:px-2 file:rounded-sm file:border-0 file:bg-elevated file:text-text-muted file:cursor-pointer"
              disabled={uploading}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-subtle font-mono">:</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value.toLowerCase())}
            placeholder="emoji_name"
            className="flex-1 text-sm font-mono px-2 py-1 rounded-sm bg-bg border border-border-soft outline-hidden focus:border-accent text-text"
            disabled={uploading}
            maxLength={32}
          />
          <span className="text-xs text-text-subtle font-mono">:</span>
          <Button
            variant="primary"
            onClick={upload}
            disabled={!file || !name || uploading}
          >
            {uploading ? <Spinner size={12} /> : 'Upload'}
          </Button>
        </div>
        {error && (
          <div className="text-xs text-danger bg-danger/10 px-2 py-1 rounded-sm">{error}</div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// EmojiGrid — list of existing emoji with hover-to-delete
// =============================================================================
function EmojiGrid({ emojis }: { emojis: ServerEmoji[] }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {emojis.map((e) => (
        <EmojiCard key={e.id} emoji={e} />
      ))}
    </div>
  );
}

function EmojiCard({ emoji }: { emoji: ServerEmoji }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  async function remove() {
    setConfirming(false);
    setBusy(true);
    setError(null);
    try {
      await api.deleteServerEmoji(emoji.id);
      // List updates via WS event
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete');
      setBusy(false);
    }
  }

  return (
    <div className="bevel-down rounded-aim bg-elevated p-2 flex items-center gap-2 group">
      <img
        src={emoji.fullUrl}
        alt={`:${emoji.name}:`}
        className="h-8 w-8 object-contain shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-mono text-text truncate">:{emoji.name}:</div>
      </div>
      <button
        type="button"
        onClick={() => setConfirming(true)}
        disabled={busy}
        title={`Delete :${emoji.name}:`}
        aria-label={`Delete :${emoji.name}:`}
        className="opacity-50 hover:opacity-100 hover:text-danger transition-all p-1 shrink-0"
      >
        {busy ? <Spinner size={12} /> : <Icon name="trash" size={12} />}
      </button>
      <ConfirmDialog
        open={confirming}
        title="Delete emoji"
        body={
          <>
            Delete <span className="font-mono">:{emoji.name}:</span> from this server? Messages
            already using it will show the shortcode instead.
          </>
        }
        confirmLabel="Delete"
        destructive
        onConfirm={() => void remove()}
        onCancel={() => setConfirming(false)}
      />
      {error && (
        <div
          className="absolute mt-12 text-[10px] text-danger bg-bg border border-danger px-1 rounded-sm"
          role="alert"
        >
          {error}
        </div>
      )}
    </div>
  );
}
